import {
  autoUpdate,
  flip,
  offset,
  ReferenceType,
  size,
  useClick,
  useDismiss,
  useFloating,
  UseFloatingReturn,
  useInteractions,
  UseInteractionsReturn,
  useListNavigation,
  useRole,
} from '@floating-ui/react'
import { useRef, useState } from 'react'

interface UseFloatingDropdownReturn
  extends UseFloatingReturn<ReferenceType>,
    UseInteractionsReturn {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  elementsRef: React.MutableRefObject<(HTMLElement | null)[]>
  labelsRef: React.MutableRefObject<(string | null)[]>
  activeIndex: number | null
}

export const useFloatingDropdown = ({
  defaultIsOpen,
  selectedIndex,
  disabled,
  dropdownMaxHeight,
}: {
  defaultIsOpen: boolean
  selectedIndex?: number | null
  disabled: boolean
  dropdownMaxHeight?: string
}): UseFloatingDropdownReturn => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen)
  const { refs, floatingStyles, context, ...floatingProps } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight, availableWidth }) {
          const listElement = elements.floating.children[1] as HTMLElement
          if (listElement) {
            listElement.style.maxHeight = dropdownMaxHeight ?? `${availableHeight}px`
          }
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxWidth: `${availableWidth}px`,
          })
        },
      }),
    ],
  })

  const elementsRef = useRef<Array<HTMLElement | null>>([])
  const labelsRef = useRef<Array<string | null>>([])

  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    selectedIndex,
    enabled: !disabled,
  })

  const click = useClick(context, { enabled: !disabled })
  const dismiss = useDismiss(context, { enabled: !disabled })
  const role = useRole(context, { role: 'listbox', enabled: !disabled })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    listNav,
    click,
    dismiss,
    role,
  ])
  return {
    refs,
    floatingStyles,
    context,
    isOpen,
    setIsOpen,
    elementsRef,
    labelsRef,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    ...floatingProps,
  }
}
